/**
 * Locked-screen visuals copied from the email sign step.
 * Package buttons (filled primary), not the Joshua shell's `btn-add`.
 * The popup is the only layer Escape or a backdrop may close.
 */

import { useLayoutEffect, useRef } from "react";
import { usePdfPreview, useSignatureCanvas } from "@fibonaccitec/assinatura/react";
import type { PresencialArea } from "@/features/proposta/services/proposta-assinatura.service";

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const PRIMARY = "#154680";
const TEXT = "#1a1a1a";
const MUTED = "#6b7280";
const BORDER = "#e5e7eb";
const SURFACE = "#ffffff";
const SURFACE_ALT = "#f8fafc";
const SUCCESS = "#16a34a";
const DANGER = "#dc2626";
const RADIUS = 10;
const MAX_PDF_WIDTH = 880;

type ButtonVariant = "primary" | "secondary" | "ghost";

function KioskButton(props: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  autoFocus?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
}): React.JSX.Element {
  const variant = props.variant ?? "primary";
  const palette: Record<ButtonVariant, React.CSSProperties> = {
    primary: { background: PRIMARY, color: "#ffffff", border: "none" },
    secondary: { background: SURFACE, color: TEXT, border: `1px solid ${BORDER}` },
    ghost: { background: "transparent", color: MUTED, border: "none" },
  };
  return (
    <button
      ref={props.buttonRef}
      type="button"
      onClick={() => {
        if (props.disabled) return;
        props.onClick?.();
      }}
      disabled={props.disabled}
      autoFocus={props.autoFocus}
      style={{
        ...palette[variant],
        font: `600 15px/1 ${FONT}`,
        padding: "12px 18px",
        borderRadius: RADIUS,
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.5 : 1,
      }}
    >
      {props.children}
    </button>
  );
}

function progressHint(remaining: number): string {
  return remaining === 1
    ? "Falta 1 área para concluir."
    : `Faltam ${remaining} áreas para concluir.`;
}

export function KioskHandback(props: { onRelease: () => void }): React.JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    buttonRef.current?.focus();
  }, []);
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: SURFACE_ALT,
        fontFamily: FONT,
      }}
    >
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <h1 style={{ margin: "0 0 8px", font: `700 22px/1.3 ${FONT}`, color: TEXT }}>
          Devolva o iPad
        </h1>
        <p style={{ margin: "0 0 20px", font: `400 15px/1.5 ${FONT}`, color: MUTED }}>
          Entregue o aparelho a quem conduz a assinatura.
        </p>
        <KioskButton buttonRef={buttonRef} autoFocus onClick={props.onRelease}>
          Recebi o iPad
        </KioskButton>
      </div>
    </div>
  );
}

/** Name on screen, confirm unavailable, return enabled. Optional retry. */
export function KioskStatus(props: {
  nome: string;
  brandName: string;
  message?: string | null;
  onReturn: () => void;
  onRetry?: () => void;
}): React.JSX.Element {
  return (
    <div style={shellStyle()}>
      <TopBar nome={props.nome} brandName={props.brandName} signed={0} total={0} />
      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        {props.message ? (
          <p role="alert" style={{ margin: 0, color: TEXT, font: `500 15px/1.4 ${FONT}` }}>
            {props.message}
          </p>
        ) : (
          <p style={{ margin: 0, color: MUTED, font: `500 15px/1.4 ${FONT}` }}>
            Abrindo a proposta…
          </p>
        )}
      </main>
      <footer style={footerStyle()}>
        <span />
        <div style={{ display: "flex", gap: 10 }}>
          {props.onRetry ? (
            <KioskButton variant="secondary" onClick={props.onRetry}>
              Tentar novamente
            </KioskButton>
          ) : null}
          <KioskButton variant="secondary" onClick={props.onReturn}>
            Cancelar
          </KioskButton>
          <KioskButton disabled>Finalizar assinatura</KioskButton>
        </div>
      </footer>
    </div>
  );
}

export function KioskDocument(props: {
  pdf: Uint8Array;
  areas: readonly PresencialArea[];
  nome: string;
  brandName: string;
  stamps: ReadonlyMap<string, string>;
  adopted: string | null;
  submitting: boolean;
  pagesReady: boolean;
  banner: string | null;
  activeArea: PresencialArea | null;
  persistKey: string;
  onAreaClick: (area: PresencialArea) => void;
  onRedraw: () => void;
  onFinalize: () => void;
  onReturn: () => void;
  onModalConfirm: (pngBase64: string) => void;
  onModalClose: () => void;
  onPagesReady: () => void;
  onPreviewError: () => void;
}): React.JSX.Element {
  const signed = props.areas.filter((area) => props.stamps.has(area.id)).length;
  const total = props.areas.length;
  const allSigned = total > 0 && signed === total && props.adopted != null;
  const canClear = props.stamps.size > 0 || props.adopted != null;

  return (
    <div style={shellStyle()}>
      <TopBar
        nome={props.nome}
        brandName={props.brandName}
        signed={signed}
        total={total}
        canRedraw={canClear}
        redrawDisabled={props.submitting}
        onRedraw={props.onRedraw}
      />
      <PdfPages
        pdf={props.pdf}
        areas={props.areas}
        stamps={props.stamps}
        pagesReady={props.pagesReady}
        submitting={props.submitting}
        onAreaClick={props.onAreaClick}
        onPagesReady={props.onPagesReady}
        onPreviewError={props.onPreviewError}
      />
      <footer style={footerStyle()}>
        <div
          style={{
            font: `500 12px/1.3 ${FONT}`,
            color: allSigned ? SUCCESS : MUTED,
            minWidth: 0,
          }}
        >
          {allSigned ? `${signed} de ${total} áreas assinadas` : progressHint(Math.max(total - signed, 0))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <KioskButton
            variant="secondary"
            disabled={props.submitting}
            onClick={props.onReturn}
          >
            Cancelar
          </KioskButton>
          <KioskButton
            disabled={!allSigned || props.submitting || !props.pagesReady}
            onClick={props.onFinalize}
          >
            Finalizar assinatura
          </KioskButton>
        </div>
      </footer>
      {props.banner ? (
        <div
          role="alert"
          style={{
            position: "fixed",
            top: 80,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 60,
            background: DANGER,
            color: "#ffffff",
            padding: "10px 16px",
            borderRadius: RADIUS,
            font: `500 13px/1.4 ${FONT}`,
            maxWidth: 480,
          }}
        >
          {props.banner}
        </div>
      ) : null}
      {props.activeArea ? (
        <SignPopup
          area={props.activeArea}
          persistKey={props.persistKey}
          onConfirm={props.onModalConfirm}
          onClose={props.onModalClose}
        />
      ) : null}
    </div>
  );
}

function TopBar(props: {
  nome: string;
  brandName: string;
  signed: number;
  total: number;
  canRedraw?: boolean;
  redrawDisabled?: boolean;
  onRedraw?: () => void;
}): React.JSX.Element {
  const complete = props.total > 0 && props.signed === props.total;
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        alignItems: "center",
        gap: 16,
        padding: "12px 20px",
        background: SURFACE,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <strong style={{ font: `700 15px/1 ${FONT}`, color: TEXT }}>
        {props.brandName}
      </strong>
      <div
        style={{
          textAlign: "center",
          font: `500 13px/1.3 ${FONT}`,
          color: MUTED,
        }}
      >
        {`Assinatura de ${props.nome}`}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {props.total > 0 ? (
          <span
            aria-label={`${props.signed} de ${props.total} áreas assinadas`}
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              background: complete ? SUCCESS : SURFACE_ALT,
              color: complete ? "#ffffff" : TEXT,
              font: `700 12px/1 ${FONT}`,
              border: complete ? "none" : `1px solid ${BORDER}`,
            }}
          >
            {props.signed}/{props.total}
          </span>
        ) : null}
        {props.canRedraw ? (
          <button
            type="button"
            disabled={props.redrawDisabled}
            onClick={() => {
              if (props.redrawDisabled) return;
              props.onRedraw?.();
            }}
            style={{
              background: "transparent",
              border: "none",
              cursor: props.redrawDisabled ? "not-allowed" : "pointer",
              padding: "6px 10px",
              borderRadius: RADIUS,
              font: `500 12px/1 ${FONT}`,
              color: MUTED,
              opacity: props.redrawDisabled ? 0.5 : 1,
            }}
          >
            Recomeçar
          </button>
        ) : null}
      </div>
    </header>
  );
}

function PdfPages(props: {
  pdf: Uint8Array;
  areas: readonly PresencialArea[];
  stamps: ReadonlyMap<string, string>;
  pagesReady: boolean;
  submitting: boolean;
  onAreaClick: (area: PresencialArea) => void;
  onPagesReady: () => void;
  onPreviewError: () => void;
}): React.JSX.Element {
  const preview = usePdfPreview(props.pdf);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
  const geos = useRef<Array<{ pageWidthPt: number; pageHeightPt: number } | null>>([]);
  const reported = useRef(false);
  const onPagesReady = useRef(props.onPagesReady);
  const onPreviewError = useRef(props.onPreviewError);
  onPagesReady.current = props.onPagesReady;
  onPreviewError.current = props.onPreviewError;

  useLayoutEffect(() => {
    if (preview.error) {
      onPreviewError.current();
      return;
    }
    if (preview.loading || preview.numPages === 0 || reported.current) return;
    let cancelled = false;
    const paint = async (): Promise<void> => {
      try {
        for (let index = 0; index < preview.numPages; index += 1) {
          const canvas = canvasRefs.current[index];
          if (!canvas) throw new Error("page canvas is not mounted");
          const geo = await preview.renderPage(canvas, index, MAX_PDF_WIDTH);
          if (cancelled) return;
          geos.current[index] = geo;
        }
        if (!cancelled) {
          reported.current = true;
          onPagesReady.current();
        }
      } catch {
        if (!cancelled) onPreviewError.current();
      }
    };
    void paint();
    return () => {
      cancelled = true;
    };
  }, [preview.error, preview.loading, preview.numPages, preview.renderPage]);

  const pageCount = preview.numPages;
  return (
    <main style={{ flex: 1, overflow: "auto", background: "#f1f5f9", padding: "24px 16px" }}>
      {Array.from({ length: pageCount }, (_, pageIndex) => {
        const sample = props.areas.find((area) => area.pageIndex === pageIndex);
        const geo = geos.current[pageIndex];
        const width = geo?.pageWidthPt ?? sample?.pageWidthPt ?? 595;
        const height = geo?.pageHeightPt ?? sample?.pageHeightPt ?? 842;
        const pageAreas = props.areas.filter((area) => area.pageIndex === pageIndex);
        return (
          <div
            key={pageIndex}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: MAX_PDF_WIDTH,
              margin: "0 auto 16px",
              aspectRatio: `${width} / ${height}`,
              background: SURFACE,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
            }}
          >
            <canvas
              ref={(node) => {
                canvasRefs.current[pageIndex] = node;
              }}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
            {pageAreas.map((area) => (
              <AreaBox
                key={area.id}
                area={area}
                png={props.stamps.get(area.id) ?? null}
                locked={!props.pagesReady || props.submitting}
                onClick={props.onAreaClick}
              />
            ))}
          </div>
        );
      })}
    </main>
  );
}

function AreaBox(props: {
  area: PresencialArea;
  png: string | null;
  locked: boolean;
  onClick: (area: PresencialArea) => void;
}): React.JSX.Element {
  const { area } = props;
  const left = (area.xPt / area.pageWidthPt) * 100;
  const width = (area.widthPt / area.pageWidthPt) * 100;
  const height = (area.heightPt / area.pageHeightPt) * 100;
  const top = ((area.pageHeightPt - area.yPt - area.heightPt) / area.pageHeightPt) * 100;
  const stamped = props.png != null;
  const empty = !stamped;
  return (
    <div
      role={empty ? "button" : undefined}
      aria-label={empty ? "Toque para assinar" : undefined}
      aria-disabled={empty && props.locked ? true : undefined}
      tabIndex={empty && !props.locked ? 0 : -1}
      onClick={() => {
        if (!empty || props.locked) return;
        props.onClick(area);
      }}
      onKeyDown={(event) => {
        if (!empty || props.locked) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          props.onClick(area);
        }
      }}
      style={{
        position: "absolute",
        left: `${left}%`,
        top: `${top}%`,
        width: `${width}%`,
        height: `${height}%`,
        boxSizing: "border-box",
        border: `2px ${empty ? "dashed" : "solid"} ${PRIMARY}`,
        background: empty ? "transparent" : "rgba(21, 70, 128, 0.08)",
        borderRadius: 4,
        cursor: empty && !props.locked ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {props.png ? (
        <img
          src={`data:image/png;base64,${props.png}`}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }}
        />
      ) : null}
      {empty ? (
        <span
          style={{
            font: `600 11px/1.2 ${FONT}`,
            color: PRIMARY,
            background: "rgba(255, 255, 255, 0.92)",
            padding: "4px 8px",
            borderRadius: 4,
            pointerEvents: "none",
          }}
        >
          Toque para assinar
        </span>
      ) : null}
    </div>
  );
}

function SignPopup(props: {
  area: PresencialArea;
  persistKey: string;
  onConfirm: (pngBase64: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pad = useSignatureCanvas(canvasRef, {
    persistKey: `${props.persistKey}:area:${props.area.id}`,
    penColor: "#1a1a1a",
    maxBackingWidth: 1200,
    maxBackingHeight: 600,
  });

  useLayoutEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.onClose]);

  const confirm = (): void => {
    const dataUrl = pad.getDataUrl();
    if (!dataUrl) return;
    props.onConfirm(dataUrl.slice(dataUrl.indexOf(",") + 1));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Assine no quadro abaixo"
      onClick={props.onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          background: SURFACE,
          borderRadius: RADIUS + 4,
          padding: 22,
          maxWidth: 600,
          width: "100%",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
          fontFamily: FONT,
        }}
      >
        <h2 style={{ margin: "0 0 4px", font: `700 17px/1.3 ${FONT}`, color: TEXT }}>
          Assine no quadro abaixo
        </h2>
        <p style={{ margin: "0 0 16px", font: `400 13px/1.5 ${FONT}`, color: MUTED }}>
          Use o mouse ou toque para desenhar sua assinatura.
        </p>
        <div
          style={{
            position: "relative",
            border: `1px solid ${BORDER}`,
            borderRadius: RADIUS,
            marginBottom: 8,
          }}
        >
          <canvas
            ref={canvasRef}
            aria-label="Assine no quadro abaixo"
            style={{
              display: "block",
              width: "100%",
              height: 240,
              touchAction: "none",
              borderRadius: RADIUS,
            }}
          />
          {!pad.hasInk ? (
            <span
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: `400 13px/1 ${FONT}`,
                color: MUTED,
                pointerEvents: "none",
              }}
            >
              Comece a desenhar aqui
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <KioskButton variant="ghost" onClick={pad.clear}>
            Limpar
          </KioskButton>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <KioskButton variant="secondary" onClick={props.onClose}>
            Voltar
          </KioskButton>
          <KioskButton disabled={!pad.hasInk} onClick={confirm}>
            Confirmar
          </KioskButton>
        </div>
      </div>
    </div>
  );
}

function shellStyle(): React.CSSProperties {
  return {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    background: SURFACE,
    fontFamily: FONT,
    color: TEXT,
  };
}

function footerStyle(): React.CSSProperties {
  return {
    position: "sticky",
    bottom: 0,
    zIndex: 30,
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 16,
    padding: "12px 20px",
    background: SURFACE,
    borderTop: `1px solid ${BORDER}`,
  };
}
